import React, { FC } from 'react';

import { Box, Flex, HStack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { DownloadIconButton, PrimaryIconButton } from 'components/DataEntry';
import {
  ComplianceIcon,
  DependenciesIcon,
  ExitFullscreenIcon,
  FullscreenIcon,
  TableIcon,
  Tag,
} from 'components/index';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';

interface Props {
  isTableView: boolean;
  isFullWindow?: boolean;
  onToggleFullWindow?: () => void;
  onToggleTable: () => void;
  onShowGraph: () => void;
  onShowPrincipals: () => void;
  onShowAccessGraph: () => void;
  onDownload?: () => void;
}

export const ActivityGraphControls: FC<Props> = ({
  isTableView,
  isFullWindow,
  onToggleFullWindow,
  onToggleTable,
  onShowGraph,
  onShowPrincipals,
  onShowAccessGraph,
  onDownload,
}) => {
  const { activityIdentityCount, activityAccessesDownload } = useSelector(
    selectIncidentResponse,
  );

  return (
    <Flex justify="flex-end" w="full">
      <HStack>
        <Tag
          label={
            <HStack>
              <Box>
                {activityIdentityCount.isLoading ? (
                  <CustomSpinner />
                ) : (
                  activityIdentityCount.data
                )}
              </Box>
              <Box>
                {activityIdentityCount.data === 1 ? ' identity' : ' identities'}
              </Box>
            </HStack>
          }
          styles={{
            label: { color: customTheme.colors.blue['300'] },
            tag: {
              borderColor: customTheme.colors.blue['300'],
              borderRadius: 'full',
              bg: colorOpacity(customTheme.colors.blue['300'], 0.1),
              px: 3,
              border: '0.5px solid',
            },
          }}
        />
        <Box
          mx={1}
          h="full"
          borderRight="1px solid"
          borderColor="componentBorder"
          py={0.5}
        />

        {!isTableView && onToggleFullWindow && (
          <PrimaryIconButton
            aria-label="toggle full map"
            icon={isFullWindow ? <ExitFullscreenIcon /> : <FullscreenIcon />}
            tooltip={
              isFullWindow ? 'Exit Full Window' : 'Show Graph in Full Window'
            }
            onClick={onToggleFullWindow}
            color={customTheme.colors.gray['250']}
            bg="white"
          />
        )}
        {isTableView ? (
          <PrimaryIconButton
            aria-label="graph view"
            icon={<DependenciesIcon />}
            tooltip="Show As Graph"
            onClick={onShowGraph}
            onMouseDown={event => event.preventDefault()}
            color={customTheme.colors.gray['250']}
            bg="white"
          />
        ) : (
          <PrimaryIconButton
            aria-label="table view"
            icon={<TableIcon />}
            tooltip="Table view"
            onClick={onToggleTable}
            onMouseDown={event => event.preventDefault()}
            color={customTheme.colors.gray['250']}
            bg="white"
            tooltipW={16}
          />
        )}
        <PrimaryIconButton
          aria-label="show principals with access"
          icon={<ComplianceIcon />}
          tooltip="Show principals with access"
          onClick={onShowPrincipals}
          color={customTheme.colors.gray['250']}
          bg="white"
        />
        {/* The CSV download comes from the API, so it works in graph and
            table views and shows a spinner while the export is in progress. */}
        {onDownload && (
          <DownloadIconButton
            aria-label="download-activity-graph"
            tooltip="Download Activity Graph"
            onClick={onDownload}
            isLoading={activityAccessesDownload.isLoading}
            isDisabled={activityAccessesDownload.isLoading}
            color={customTheme.colors.gray['250']}
            bg="white"
          />
        )}
      </HStack>
    </Flex>
  );
};
